import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

// Cook telemetry — per-step start/finish timestamps written to
// cook_sessions / cook_session_steps (migration 0136) so the app can
// eventually calibrate prep-notification lead times against real data.
//
// Also schedules mid-cook timer pushes via cook_step_notifications
// (migration 0137): when a step with a timer starts, a row gets
// queued with deliver_at = now + timer_seconds. The drain RPC fires
// it as a Web Push so the user can close the app during a 30-min
// braise and get pinged when it's time to act. finishStep / endCook
// delete any still-pending row so a user who advances early doesn't
// get a "flip the steak" push three minutes after they already did.
//
// Distinct from useCookSession.js (shared override state between
// CookMode and CookComplete — swaps, extras). This hook ONLY deals
// with durations + timer pushes; nothing it writes affects the live UI.
//
// The API is intentionally imperative. CookMode calls startCook() at
// the top of the cook flow, startStep() each time the user advances,
// finishStep() on timer-ring or manual tap, and endCook() on either
// finalize or abandon. Every method is fire-and-forget from the
// caller's perspective — failures log and swallow so the kitchen
// flow is never blocked by a logging hiccup.

/**
 * useCookTelemetry(userId)
 *   → { session, activeStep, startCook, startStep, finishStep, endCook }
 *
 * `session` is the current cook_sessions row (null until startCook).
 * `activeStep` is the most recent cook_session_steps row without a
 * finished_at, so UI can read "currently on step N" without tracking
 * its own pointer.
 */
export function useCookTelemetry(userId) {
  const [session, setSession] = useState(null);
  const [activeStep, setActiveStep] = useState(null);

  // Mirror in refs so end-of-cook unmount paths can reach the current
  // value without stale-closure pain.
  const sessionRef    = useRef(null);
  const activeStepRef = useRef(null);
  useEffect(() => { sessionRef.current    = session;    }, [session]);
  useEffect(() => { activeStepRef.current = activeStep; }, [activeStep]);

  // Team-cook: when the caller is a HELPER/DINER joining a lead's
  // active session, we adopt the session (don't create new) AND gate
  // all telemetry WRITES (startStep, finishStep, endCook) so we don't
  // corrupt the lead's cook_session_steps timeline. Helpers READ via
  // realtime — the lead's writes drive everyone's UI.
  const isLeadRef = useRef(true);
  useEffect(() => {
    isLeadRef.current = session ? session.user_id === userId : true;
  }, [session, userId]);

  // In-flight startCook dedupe. Two effects in CookMode fire on the
  // same render commit when view→"cook": the mount-startCook effect
  // (CookMode.jsx:597) and the step-auto-start effect (line 629) whose
  // internal call to startCook fires as a helper-safety-net when no
  // session exists yet. Both saw sessionRef as null between their
  // setState beats and both raced SELECT-then-INSERT against the DB,
  // creating two cook_sessions rows for the same (user, recipe). The
  // ref makes the second call return the SAME promise the first is
  // already awaiting — they end up with the same row.
  const inflightStartRef = useRef(null);

  // `servings` is EXPLICIT user intent only (the stepper pick / a
  // seeded serving count) — pass null/undefined when the user hasn't
  // chosen. `defaultServings` is the fallback used ONLY for a fresh
  // INSERT. The split matters on resume: banner/cold-start remounts
  // used to pass the base recipe's serves as `servings`, and the
  // latest-intent-wins patch below clobbered the session's saved 72
  // back to 4 before the resume-sync effect could restore it.
  const startCook = useCallback(async ({ recipeSlug, recipeTitle, recipeEmoji, servings, defaultServings, cookParams, joinSessionId, resumeSessionId, steps, nudgeAfterMs = null } = {}) => {
    if (!userId) return null;
    // Same-recipe in-flight dedupe. Join paths get their own promise
    // (different target session entirely), so key by joinSessionId
    // when present, else recipeSlug. Keeps two parallel cooks of
    // DIFFERENT recipes from blocking each other.
    const dedupeKey = joinSessionId || resumeSessionId || recipeSlug || "_anon";
    const inflight = inflightStartRef.current;
    if (inflight && inflight.key === dedupeKey) {
      return inflight.promise;
    }
    const promise = (async () => {
      // Lapsed-resume path — adopt the caller's OWN session by id,
      // bypassing the 2h re-find below. Without this, FINISH & LOG on
      // a days-old lapsed cook minted a FRESH session: the user's
      // log landed on the new one while the original stayed 'active'
      // forever as a zombie prompt.
      if (resumeSessionId) {
        const { data: target } = await supabase
          .from("cook_sessions")
          .select("*")
          .eq("id", resumeSessionId)
          .eq("user_id", userId)
          .maybeSingle();
        if (target && target.status === "active") {
          setSession(target);
          return target;
        }
        // Session resolved meanwhile (another device?) — fall through
        // to the normal flow.
      }
      // Team-cook join path — caller passed an explicit session ID
      // (deep-link from a participant notification). Adopt that
      // session if it's still active. Skip all the "create new"
      // logic below — helpers never create rows in cook_sessions.
      if (joinSessionId) {
        const { data: target } = await supabase
          .from("cook_sessions")
          .select("*")
          .eq("id", joinSessionId)
          .maybeSingle();
        if (target && target.status === "active") {
          setSession(target);
          return target;
        }
        // Target session is gone/ended → fall through to normal flow
        // (will resume my own session for this recipe if I have one,
        // else create a fresh one).
      }

      if (!recipeSlug) return null;
      // Inline step snapshot (migration 0235) — computed up here
      // because BOTH the fresh-insert and the adopt-existing paths
      // need it. Lets CookActivityCard and the Live Activity pusher
      // render steps + timers without resolving the underlying
      // recipe — survives user_recipes cache lag, slug drift
      // (regenerated titles), and recipe deletion. Compact shape —
      // only the fields the renderers read.
      const stepSummary = Array.isArray(steps) && steps.length > 0
        ? steps.map((s) => ({
            id:     s?.id != null ? String(s.id) : null,
            title:  typeof s?.title === "string" ? s.title : "",
            // Snapshot the prose too — when a recipe row goes missing,
            // banner resume reconstructs the cook from this and a
            // title-only step ("Season the steaks") is useless. Capped so
            // the cook_sessions row stays reasonable.
            instruction: typeof s?.instruction === "string" ? s.instruction.slice(0, 600) : "",
            timer:  Number(s?.timer) || 0,
            ...(Array.isArray(s?.timers) && s.timers.length > 0
              ? { timers: s.timers.map((t) => ({ seconds: Number(t?.seconds) || 0, label: t?.label || null })) }
              : {}),
          }))
        : null;
      // Resume if an active session for this user + recipe was opened
      // within the last 2 hours. Covers page refresh mid-cook, tab
      // swap, accidental navigation away, browser crash. Window cap
      // keeps long-abandoned active sessions from being resurrected
      // the next time the user cooks the same dish.
      const twoHoursAgo = new Date(Date.now() - 2 * 3600 * 1000).toISOString();
      const { data: existing } = await supabase
        .from("cook_sessions")
        .select("*")
        .eq("user_id", userId)
        .eq("recipe_slug", recipeSlug)
        .eq("status", "active")
        .gte("started_at", twoHoursAgo)
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      // Team-cook auto-detect: am I a participant on someone ELSE'S
      // active session for this recipe? If yes, adopt theirs instead
      // of creating my own. Covers the case where a helper opens
      // CookMode against a recipe they were invited to but don't
      // own. The lead's session is the source of truth — helpers
      // never run a parallel cook of the same dish.
      if (!existing?.id) {
        const { data: teamRows } = await supabase
          .from("cook_session_participants")
          .select("cook_session_id, cook_sessions!inner(id, status, recipe_slug, started_at, user_id, recipe_title, recipe_emoji, servings, cook_params, step_summary)")
          .eq("user_id", userId)
          .eq("cook_sessions.status", "active")
          .eq("cook_sessions.recipe_slug", recipeSlug)
          .gte("cook_sessions.started_at", twoHoursAgo);
        const teamSession = (teamRows || [])
          .map((r) => r.cook_sessions)
          .filter((s) => s && s.user_id !== userId)
          .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))[0];
        if (teamSession) {
          setSession(teamSession);
          return teamSession;
        }
      }

      if (existing?.id) {
        // Adopt the session without restoring the step pointer. The
        // consumer's own activeStep state drives which step is shown;
        // when they advance, our startStep auto-finishes any lingering
        // open rows from before the refresh so cook_duration_stats
        // stays clean. Attempting to auto-resume to the "right" step
        // index fights React's effect ordering in the consumer for
        // minimal UX gain.
        //
        // If the caller provided a fresh EXPLICIT servings value AND
        // the existing row's servings disagrees, patch the row so the
        // user's most recent override sticks. Common path: user
        // bumped servings on overview, tapped START, minimized,
        // came back, bumped again, tapped START again. The latest
        // intent wins. `defaultServings` is deliberately NOT consulted
        // here — a resume remount carries no intent, and patching the
        // default would clobber the session's saved choice.
        const targetServes = Number.isFinite(Number(servings)) && Number(servings) > 0 ? Number(servings) : null;
        const patch = {};
        if (targetServes != null && existing.servings !== targetServes) patch.servings = targetServes;
        // Same latest-intent-wins rule for the cook's tweak params: if the
        // caller passed a doneness set that differs from what's stored,
        // persist it. On a plain resume the caller passes back the SAME
        // params it just restored → JSON-equal → no write.
        if (cookParams && Object.keys(cookParams).length
            && JSON.stringify(existing.cook_params ?? null) !== JSON.stringify(cookParams)) {
          patch.cook_params = cookParams;
        }
        // Backfill the inline step snapshot on adopted sessions that
        // lack one — sessions created by paths that don't thread
        // steps (scheduled/push-to-start) stayed null forever, which
        // starved the Live Activity pusher of its step/timer map
        // whenever the recipe-slug lookup also missed (slug drift).
        if (!existing.step_summary && stepSummary) {
          patch.step_summary = stepSummary;
        }
        if (Object.keys(patch).length > 0) {
          const { data: patched } = await supabase
            .from("cook_sessions")
            .update(patch)
            .eq("id", existing.id)
            .select()
            .single();
          if (patched) {
            setSession(patched);
            return patched;
          }
        }
        setSession(existing);
        return existing;
      }

      // New session insert. servings is nullable; only include the
      // key when the caller passed a finite positive int so older
      // DBs without the column (pre-0152) don't 400 on the unknown
      // field. Same conditional-spread pattern useScheduledMeals uses.
      // Fresh inserts take the explicit pick when present, else the
      // caller's default (the recipe's effective serves).
      const explicitServes = Number.isFinite(Number(servings)) && Number(servings) > 0 ? Number(servings) : null;
      const fallbackServes = Number.isFinite(Number(defaultServings)) && Number(defaultServings) > 0 ? Number(defaultServings) : null;
      const targetServes = explicitServes ?? fallbackServes;
      // (stepSummary computed above, before the adopt-existing branch,
      // so adopted sessions can backfill it too.)
      const { data, error } = await supabase
        .from("cook_sessions")
        .insert({
          user_id:      userId,
          recipe_slug:  recipeSlug,
          recipe_title: recipeTitle || null,
          recipe_emoji: recipeEmoji || null,
          status:       "active",
          ...(targetServes != null ? { servings: targetServes } : {}),
          ...(stepSummary ? { step_summary: stepSummary } : {}),
          // Per-cook tweaks (doneness, etc.) live on the cook itself
          // (migration 0273) so resume restores the exact augmented cook
          // instead of re-deriving from the central recipe + localStorage.
          ...(cookParams && Object.keys(cookParams).length ? { cook_params: cookParams } : {}),
        })
        .select()
        .single();
      if (error) { console.warn("[cookTelemetry] startCook failed:", error); return null; }
      // Finish-nudge — one server-side push per FRESH session, queued
      // for the moment the cook's live window lapses (+15 min grace).
      // Rides the existing cook_step_notifications drain (per-minute
      // pg_cron → notifications → push fan-out, 'cook_step_timers'
      // opt-out applies) so a cook that goes quiet gets an OS-level
      // "Did you finish X?" even if the app never reopens. The row
      // dies with the session: endCook purges undelivered rows on
      // BOTH finish and abandon, and the lapsed in-app banner flips
      // at the same timestamp, so push and banner tell one story.
      // step_row_id stays null → the pause/resume/reset re-pin paths
      // (keyed on step_row_id) never touch it. Resumed sessions skip
      // this branch (their original nudge row is still queued).
      if (Number.isFinite(Number(nudgeAfterMs)) && Number(nudgeAfterMs) > 0) {
        const windowMs = Number(nudgeAfterMs);
        supabase.from("cook_step_notifications").insert({
          user_id:         userId,
          cook_session_id: data.id,
          step_row_id:     null,
          step_id:         "finish-nudge",
          step_title:      null,
          recipe_title:    recipeTitle || null,
          recipe_emoji:    recipeEmoji || null,
          body:            `Did you finish ${recipeTitle || "your cook"}? Tap to log it — or clear it if plans changed.`,
          timer_seconds:   Math.max(1, Math.round(windowMs / 1000)),
          deliver_at:      new Date(Date.now() + windowMs + 15 * 60 * 1000).toISOString(),
        }).then(({ error: nudgeErr }) => {
          if (nudgeErr) console.warn("[cookTelemetry] finish-nudge queue failed:", nudgeErr.message);
        });
      }
      setSession(data);
      return data;
    })().catch((e) => {
      console.warn("[cookTelemetry] startCook threw:", e);
      return null;
    }).finally(() => {
      // Clear in-flight slot ONLY if it's still our promise (a fast
      // re-entry for a different recipe may have replaced it already).
      if (inflightStartRef.current?.promise === promise) {
        inflightStartRef.current = null;
      }
    });
    inflightStartRef.current = { key: dedupeKey, promise };
    return promise;
  }, [userId]);

  const startStep = useCallback(async ({ stepId, stepTitle, nominalSeconds, timerBody, recipeSlug, recipeTitle, recipeEmoji, servings, defaultServings } = {}) => {
    // Multi-helper model (migration 0215): the RLS on
    // cook_session_steps now enforces "session-owner OR active
    // assignee" at the DB level. Client doesn't need to gate by
    // session.user_id — helpers writing their own assigned step
    // rows is the supported path. RLS rejects unauthorized writes;
    // we just attempt and surface errors via console.warn.
    let cur = sessionRef.current;
    // Auto-start the session if the first telemetry event arrives without
    // anyone having tapped "START COOKING" — happens whenever a helper
    // marks their assigned step done, or the user advances directly from
    // a notification tap. Telemetry IS the start signal; the button is a
    // manual override, not a precondition. (Team-cooks plan §4b.)
    if (!cur?.id) {
      if (!recipeSlug) {
        // Without a slug we can't materialize a cook_sessions row. Caller
        // didn't thread the recipe context — log and bail rather than
        // inserting a stub row we can't recover.
        console.warn("[cookTelemetry] startStep with no session AND no recipeSlug — skipping auto-start");
        return null;
      }
      cur = await startCook({ recipeSlug, recipeTitle, recipeEmoji, servings, defaultServings });
      if (!cur?.id) return null;
    }
    if (!userId || stepId == null) return null;
    try {
      // Find every open step row OWNED BY THIS USER in this session.
      // Scoped to user_id so helpers' parallel work survives when the
      // lead advances their own cursor. Pre-migration-0215 this query
      // unscoped, which torched co-cooks' open timers every time the
      // lead jumped steps.
      const { data: openRows } = await supabase
        .from("cook_session_steps")
        .select("id, step_id")
        .eq("cook_session_id", cur.id)
        .eq("user_id", userId)
        .is("finished_at", null);

      // RESUME-AWARE: if there's already an open row for THIS step
      // (user minimized → reopened, OR user navigated to a later step
      // and came back), adopt it. Don't close it, don't insert a new
      // one, don't touch its pending notification — the live deadline
      // must survive the remount or the user sees their timer reset
      // to full duration, which is jarring and wrong.
      const stepIdStr = String(stepId);
      const existingForThisStep = (openRows || []).find(r => String(r.step_id) === stepIdStr);
      if (existingForThisStep) {
        const { data: full } = await supabase
          .from("cook_session_steps")
          .select("*")
          .eq("id", existingForThisStep.id)
          .maybeSingle();
        if (full) {
          setActiveStep(full);
          return full;
        }
      }

      // No open row for THIS step → insert one. Open rows for OTHER
      // steps are intentionally LEFT OPEN — a multi-helper cook can
      // have several concurrent open rows (Marissa chopping while
      // Trevor sears). Pre-this-change, advancing to step 2 then back
      // to step 1 forced startStep to close step 1's open row on the
      // step-2 transition; step 1 then re-inserted fresh on return,
      // losing the timer_state. The result was "navigate away, lose
      // your timer." With auto-close removed, the row stays open and
      // the resume-aware branch above adopts it on return.
      //
      // Trade-off: abandoned step rows now linger in the DB until
      // endCook stamps them. cook_duration_stats filters to rows
      // where finished_at IS NOT NULL anyway, so analytics aren't
      // affected.

      const { data, error } = await supabase
        .from("cook_session_steps")
        .insert({
          cook_session_id: cur.id,
          user_id:         userId,
          step_id:         String(stepId),
          step_title:      stepTitle || null,
          nominal_seconds: Number.isFinite(nominalSeconds) ? nominalSeconds : null,
        })
        .select()
        .single();
      if (error) { console.warn("[cookTelemetry] startStep failed:", error); return null; }
      setActiveStep(data);

      // Queue a timer push if this step has a countdown. No quiet-hours
      // shift applied — the user is actively cooking, pings are the
      // point. Body: "Step 4: Flip the steak" unless the caller passed
      // something richer.
      //
      // Density filter — the prompt now asks Claude to emit a timer
      // on EVERY step with a clockable duration (sears, sautés, bakes,
      // rests), so the display shows "~6 min" on active steps too.
      // That would push-notify on every short step by default, which
      // is noisy for cooks standing at the stove. Respect the user's
      // density preference (localStorage, defaults to "all"):
      //   "all"       — push every timer ≥ 5s  (default)
      //   "long_only" — push only timers ≥ 5 min (passive-wait)
      //   "off"       — never push; display-only
      const density = (() => {
        try { return window.localStorage.getItem("cook_timer_density") || "all"; }
        catch { return "all"; }
      })();
      const longOnlyFloor = 300; // 5 minutes
      const shouldQueuePush =
        density === "off"       ? false
      : density === "long_only" ? (Number.isFinite(nominalSeconds) && nominalSeconds >= longOnlyFloor)
      :                           (Number.isFinite(nominalSeconds) && nominalSeconds > 0);
      if (shouldQueuePush) {
        // Calibration lookup (migration 0138) — if the global corpus
        // has enough observations for this recipe+step, use the p50
        // observed duration instead of the recipe author's nominal.
        // Threshold 5 is arbitrary but reasonable: small enough that
        // popular recipes calibrate fast, large enough that one fast-
        // cook outlier doesn't drag everyone's timer low. Fallback on
        // any RPC hiccup preserves the nominal — never ring the timer
        // shorter than the recipe says just because the lookup failed.
        let effectiveSeconds = nominalSeconds;
        try {
          const { data: obs } = await supabase.rpc("observed_step_duration", {
            p_recipe_slug: cur.recipe_slug,
            p_step_id:     String(stepId),
          });
          const row = Array.isArray(obs) ? obs[0] : obs;
          if (row && Number(row.sample_count) >= 5 && Number.isFinite(Number(row.p50_seconds))) {
            effectiveSeconds = Number(row.p50_seconds);
          }
        } catch (e) {
          // Calibration is best-effort; fall through to the nominal.
          console.warn("[cookTelemetry] calibration lookup skipped:", e?.message || e);
        }

        const body = (timerBody && String(timerBody).trim())
          || (stepTitle
                ? `Timer's up — ${stepTitle}`
                : `Step ${stepId} — timer's up`);
        // Park the push in the FAR FUTURE — landing on a step must NOT
        // arm the alarm. Only tapping Start arms it: markTimerStarted
        // re-pins deliver_at to the real ring moment (now + duration). If
        // the user never starts the timer, this stays parked and never
        // fires; step-finish cleanup cancels it. (Previously this was
        // now + nominal, so just visiting a step chimed after the
        // duration even though no timer was ever started.)
        const PARKED_FUTURE_MS = 30 * 24 * 60 * 60 * 1000;
        const deliverAt = new Date(Date.now() + PARKED_FUTURE_MS).toISOString();
        const { error: pushErr } = await supabase
          .from("cook_step_notifications")
          .insert({
            user_id:         userId,
            cook_session_id: cur.id,
            step_row_id:     data.id,
            step_id:         String(stepId),
            step_title:      stepTitle || null,
            recipe_title:    cur.recipe_title || null,
            recipe_emoji:    cur.recipe_emoji || null,
            body,
            timer_seconds:   effectiveSeconds,
            deliver_at:      deliverAt,
          });
        if (pushErr) console.warn("[cookTelemetry] queue timer push failed:", pushErr);
      }

      return data;
    } catch (e) {
      console.warn("[cookTelemetry] startStep threw:", e);
      return null;
    }
  }, [userId, startCook]);

  const finishStep = useCallback(async ({ stepRowId, skipped = false, note = null } = {}) => {
    // No team-cook gate — RLS (migration 0215) accepts the update if
    // the caller wrote the row. Helpers finishing THEIR OWN assigned
    // step is the supported path. Lead finishing their own cursor
    // step works the same way.
    const id = stepRowId || activeStepRef.current?.id;
    if (!id) return null;
    try {
      const { data, error } = await supabase
        .from("cook_session_steps")
        .update({
          finished_at: new Date().toISOString(),
          skipped,
          note,
        })
        .eq("id", id)
        .select()
        .single();
      if (error) { console.warn("[cookTelemetry] finishStep failed:", error); return null; }

      // Cancel any pending timer push for this step — user advanced
      // early, the push would just be noise. Only target undelivered
      // rows so we don't leave delivered history dangling.
      await supabase.from("cook_step_notifications")
        .delete()
        .eq("step_row_id", id)
        .is("delivered_at", null);

      if (activeStepRef.current?.id === id) setActiveStep(null);
      return data;
    } catch (e) {
      console.warn("[cookTelemetry] finishStep threw:", e);
      return null;
    }
  }, []);

  const endCook = useCallback(async ({ cookLogId = null, status = "finished" } = {}) => {
    // Team-cook write gate: only the lead ends the cook. A helper
    // closing CookMode just unmounts their view; the session stays
    // live for everyone else.
    if (sessionRef.current && sessionRef.current.user_id !== userId) return null;
    const cur = sessionRef.current;
    if (!cur?.id) return null;
    try {
      // Close EVERY open step row for this session — not just the
      // one activeStepRef points at. NEXT STEP advances the cursor
      // without calling markDone, so rows for steps the user passed
      // through accumulate with finished_at=null. Downstream finalize
      // gates (kids-mode "open steps" badge, cook_duration_stats
      // join, any "all steps complete" check) all treat those as
      // still-open and refuse to consider the cook done. One sweep
      // here closes them all so the cook truly finalizes.
      //
      // skipped=true on abandon so the analytics dashboard can tell
      // an abandoned cook from a finished-but-rushed one. On a
      // legitimate finish, leave skipped at its existing value (the
      // .update only writes finished_at + skipped, and we only flip
      // skipped on abandon).
      const stepCloseUpdate = status === "abandoned"
        ? { finished_at: new Date().toISOString(), skipped: true }
        : { finished_at: new Date().toISOString() };
      await supabase.from("cook_session_steps")
        .update(stepCloseUpdate)
        .eq("cook_session_id", cur.id)
        .is("finished_at", null);

      // Purge any pending timer pushes for this session — the cook is
      // done, further pings would be nonsensical. FK cascade on session
      // delete would catch these too, but we keep the row for analytics;
      // just kill the unfired ones.
      await supabase.from("cook_step_notifications")
        .delete()
        .eq("cook_session_id", cur.id)
        .is("delivered_at", null);
      const { data, error } = await supabase
        .from("cook_sessions")
        .update({
          ended_at:    new Date().toISOString(),
          cook_log_id: cookLogId,
          status,
        })
        .eq("id", cur.id)
        .select()
        .single();
      if (error) { console.warn("[cookTelemetry] endCook failed:", error); return null; }
      setSession(null);
      setActiveStep(null);
      return data;
    } catch (e) {
      console.warn("[cookTelemetry] endCook threw:", e);
      return null;
    }
  }, []);

  // ── Timer-state writers ─────────────────────────────────────────────
  //
  // These methods sync the lead's Timer component state into
  // cook_session_steps.timer_state (migration 0214) so participants
  // see ticking / paused / reset / ended states in real time.
  //
  // Schema canonical shapes:
  //   READY:    null
  //   TICKING:  { endsAt, isPaused: false }
  //   PAUSED:   { endsAt, isPaused: true, pausedRemainingSec }
  //   ENDED:    { endsAt, isPaused: false, ended: true }
  //
  // All four methods are no-ops if there's no active step row — we
  // can't write timer_state to a row that doesn't exist yet. (For
  // single-Timer steps the row is created on step entry by startStep
  // above; for TimerStack stages it's created in onStageStart.)

  const writeTimerState = useCallback(async (timerState, explicitStepRowId = null) => {
    // No lead gate — RLS (migration 0215) lets the row's owner
    // update its timer_state. A helper running their assigned step's
    // timer should propagate pause/resume/reset to participants the
    // same way the lead does.
    //
    // explicitStepRowId wins over activeStepRef when the caller knows
    // exactly which row to write. This is load-bearing for the
    // TimerStack-stage-start race: startStage triggers an async
    // startStep INSERT, then ManualTimer mounts and synchronously
    // fires onTimerStart BEFORE setActiveStep has committed — at that
    // instant activeStepRef.current still points at the PRIOR row, so
    // a fall-back read would clobber the wrong row's timer_state.
    // ManualTimer now threads the inserted row's id through the
    // callback chain so writeTimerState targets the right row
    // regardless of the ref's race state.
    const id = explicitStepRowId || activeStepRef.current?.id;
    if (!id) return;
    try {
      const { error } = await supabase
        .from("cook_session_steps")
        .update({ timer_state: timerState })
        .eq("id", id);
      if (error) console.warn("[cookTelemetry] writeTimerState failed:", error.message);
    } catch (e) {
      console.warn("[cookTelemetry] writeTimerState threw:", e?.message || e);
    }
  }, [userId]);

  const markTimerStarted = useCallback(async ({ endsAtMs, stepRowId } = {}) => {
    if (!Number.isFinite(endsAtMs)) return;
    const endsAtIso = new Date(endsAtMs).toISOString();
    await writeTimerState({
      endsAt:   endsAtIso,
      isPaused: false,
    }, stepRowId || null);
    // Re-pin the queued lock-screen notification's deliver_at so the
    // alarm fires at the ACTUAL ring moment (now + nominal), not at
    // step-entry-time + nominal. Without this re-pin, a user who
    // reads the step for 30s before tapping Start gets the lock-
    // screen ring 30s EARLY. Scoped by step_row_id so a multi-stage
    // step doesn't accidentally re-pin sibling stages.
    const rowId = stepRowId || activeStepRef.current?.id;
    if (rowId) {
      try {
        await supabase
          .from("cook_step_notifications")
          .update({ deliver_at: endsAtIso })
          .eq("step_row_id", rowId)
          .is("delivered_at", null);
      } catch (e) {
        console.warn("[cookTelemetry] re-pin deliver_at failed:", e?.message || e);
      }
    }
  }, [writeTimerState]);

  const markTimerPaused = useCallback(async ({ remainingMs } = {}) => {
    // Read prior state to preserve endsAt so participants can render
    // the freeze-at-pause without losing the original target.
    const id = activeStepRef.current?.id;
    if (!id) return;
    let priorEndsAt = null;
    try {
      const { data } = await supabase
        .from("cook_session_steps")
        .select("timer_state")
        .eq("id", id)
        .maybeSingle();
      priorEndsAt = data?.timer_state?.endsAt || null;
    } catch { /* fall through with null */ }
    await writeTimerState({
      endsAt:             priorEndsAt,
      isPaused:           true,
      pausedRemainingSec: Math.max(0, Math.round(Number(remainingMs) / 1000)) || 0,
    });
    // Push the queued lock-screen alarm WAY into the future so the
    // drain cron doesn't fire it while the user has the timer
    // paused. We don't delete the row — the user might resume soon
    // and we'd lose the queue entry. markTimerResumed re-pins it.
    try {
      const farFuture = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      await supabase
        .from("cook_step_notifications")
        .update({ deliver_at: farFuture })
        .eq("step_row_id", id)
        .is("delivered_at", null);
    } catch (e) {
      console.warn("[cookTelemetry] pause-postpone deliver_at failed:", e?.message || e);
    }
  }, [writeTimerState]);

  const markTimerResumed = useCallback(async ({ endsAtMs } = {}) => {
    if (!Number.isFinite(endsAtMs)) return;
    const endsAtIso = new Date(endsAtMs).toISOString();
    await writeTimerState({
      endsAt:   endsAtIso,
      isPaused: false,
    });
    // Re-pin the queued notification to the NEW ring moment (post-
    // resume the deadline is now + remaining, not the original
    // pre-pause deadline).
    const id = activeStepRef.current?.id;
    if (id) {
      try {
        await supabase
          .from("cook_step_notifications")
          .update({ deliver_at: endsAtIso })
          .eq("step_row_id", id)
          .is("delivered_at", null);
      } catch (e) {
        console.warn("[cookTelemetry] resume re-pin deliver_at failed:", e?.message || e);
      }
    }
  }, [writeTimerState]);

  // Reset = user tapped the reset button on Timer. Differs from
  // markTimerEnd (timer rang) — reset returns the participant view
  // to "READY · <nominal>" pending the next user-start.
  const markTimerReset = useCallback(async () => {
    await writeTimerState(null);
    // Cancel the queued lock-screen alarm — the user discarded the
    // deadline, no ring should fire. delete only the undelivered
    // row(s) so history of past delivered notifications stays put.
    const id = activeStepRef.current?.id;
    if (id) {
      try {
        await supabase
          .from("cook_step_notifications")
          .delete()
          .eq("step_row_id", id)
          .is("delivered_at", null);
      } catch (e) {
        console.warn("[cookTelemetry] reset cancel notification failed:", e?.message || e);
      }
    }
  }, [writeTimerState]);

  // End = timer rang naturally. Keep endsAt so the participant
  // surface lands on "TIMER DONE!" instead of "READY".
  const markTimerEnded = useCallback(async () => {
    const id = activeStepRef.current?.id;
    if (!id) return;
    let priorEndsAt = null;
    try {
      const { data } = await supabase
        .from("cook_session_steps")
        .select("timer_state")
        .eq("id", id)
        .maybeSingle();
      priorEndsAt = data?.timer_state?.endsAt || new Date().toISOString();
    } catch { priorEndsAt = new Date().toISOString(); }
    await writeTimerState({
      endsAt:   priorEndsAt,
      isPaused: false,
      ended:    true,
    });
  }, [writeTimerState]);

  // Merge per-cook tweaks into the LIVE session's cook_params (migration
  // 0273). startCook only writes cook_params at session creation; this is
  // how an edit made AFTER the cook is underway (or one the creation race
  // missed) reaches the authoritative store so resume restores it. Shallow
  // top-level merge — pass { doneness: {...} } to replace the doneness
  // block while leaving sibling params (future tweaks) untouched.
  const updateCookParams = useCallback(async (partial) => {
    if (!partial || typeof partial !== "object") return null;
    const current = sessionRef.current;
    if (!current?.id) return null;
    // Helpers/diners never write to the lead's session row.
    if (!isLeadRef.current) return null;
    const merged = { ...(current.cook_params ?? {}), ...partial };
    // No-op when nothing actually changed — avoids a write (and the
    // realtime broadcast that rides it) on every restore round-trip.
    if (JSON.stringify(current.cook_params ?? null) === JSON.stringify(merged)) {
      return current;
    }
    const { data, error } = await supabase
      .from("cook_sessions")
      .update({ cook_params: merged })
      .eq("id", current.id)
      .select()
      .single();
    if (error) {
      console.warn("[cookTelemetry] updateCookParams failed:", error?.message || error);
      return null;
    }
    if (data) setSession(data);
    return data;
  }, []);

  // Persist a mid-cook servings change onto the live session row so
  // the choice survives banner resume / app restart even when the
  // user never remounts CookMode (the startCook patch only fires on
  // remount). Mirrors updateCookParams: lead-only, no-op on equality
  // so the resume-sync echo never generates a write.
  const updateServings = useCallback(async (n) => {
    const current = sessionRef.current;
    const v = Number(n);
    if (!current?.id || !Number.isFinite(v) || v <= 0) return null;
    if (!isLeadRef.current) return null;
    if (Number(current.servings) === v) return current;
    const { data, error } = await supabase
      .from("cook_sessions")
      .update({ servings: v })
      .eq("id", current.id)
      .select()
      .single();
    if (error) {
      console.warn("[cookTelemetry] updateServings failed:", error?.message || error);
      return null;
    }
    if (data) setSession(data);
    return data;
  }, []);

  // Stop EVERY live timer attached to a step — the step's own row plus
  // any TimerStack stage rows (step_id "<id>" and "<id>-s0/-s1/…"). Used
  // when the cook taps Done on an ACTIVE step: writing timer_state.ended
  // both drops the timer from the background-timer banner (which keys on
  // timer_state, not finished_at) AND makes cook-activity-push skip it,
  // so the Live Activity stops counting a sear the cook has finished.
  // PASSIVE steps (preheat / chill / rest) deliberately skip this so
  // their timer keeps running in the background.
  const endStepTimers = useCallback(async (stepId) => {
    const cur = sessionRef.current;
    if (!cur?.id || !userId || stepId == null) return;
    const base = String(stepId);
    try {
      const { data: rows } = await supabase
        .from("cook_session_steps")
        .select("id, step_id, timer_state, finished_at")
        .eq("cook_session_id", cur.id)
        .eq("user_id", userId);
      const now = new Date().toISOString();
      const targets = (rows || []).filter((r) => {
        const s = String(r.step_id);
        const mine = s === base || s.startsWith(`${base}-s`);
        const ts = r.timer_state;
        const live = ts && typeof ts === "object" && ts.endsAt && !ts.ended;
        return mine && live;
      });
      await Promise.all(targets.map((r) =>
        supabase
          .from("cook_session_steps")
          .update({
            finished_at: r.finished_at || now,
            timer_state: { ...(r.timer_state || {}), ended: true },
          })
          .eq("id", r.id),
      ));
    } catch (e) {
      console.warn("[cookTelemetry] endStepTimers threw:", e?.message || e);
    }
  }, [userId]);

  return {
    session, activeStep,
    startCook, startStep, finishStep, endCook, updateCookParams, updateServings,
    markTimerStarted, markTimerPaused, markTimerResumed, markTimerReset, markTimerEnded,
    endStepTimers,
  };
}
